import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import cx from 'classnames';
import { addToastNotification } from 'store/notification/reducer';
import IconButton from 'lib/IconButton';
import closeSvg from 'assets/icons/close.svg';
import fullscreenSvg from 'assets/icons/fullscreen.svg';
import { ButtonSize, Theme } from 'lib/IconButton/IconButton.types';
import { GoogleMapsStreetView } from 'components/common/GoogleMaps/GoogleMapsStreetView';
import { ToastProps } from 'lib/Toaster/Toaster.types';
import { StreetViewProps } from './StreetView.types';

const StreetViewUnavailableToast: ToastProps = {
  id: 'street-view-zero-results',
  header: 'Street View not available here',
  description: "We couldn't load Street View for this location. Try another spot nearby.",
  type: 'Failure',
  timeout: 2000,
};

// eslint-disable-next-line sonarjs/cognitive-complexity
export const StreetView: React.FC<StreetViewProps> = ({ center: { lat, lng }, markers, onPositionChange, close }) => {
  const dispatch = useDispatch();
  const [isFullscreen, setIsFullscreen] = useState(true);
  const [isStreetViewAvailable, setIsStreetViewAvailable] = useState(false);

  const waitForGoogle = (resolve: (value: unknown) => void, reject: (reason?: unknown) => void): void => {
    if (window.google && window.google.maps) {
      resolve(window.google.maps);
    } else {
      setTimeout(waitForGoogle.bind(this, resolve, reject), 30);
    }
  };

  const googleIsLoaded = (): Promise<unknown> => {
    return new Promise(waitForGoogle);
  };

  const checkStreetViewAvailability = (): Promise<boolean> => {
    return new Promise((resolve) => {
      const streetViewService = new window.google.maps.StreetViewService();
      const location = new window.google.maps.LatLng(lat, lng);

      streetViewService.getPanorama(
        {
          location,
          radius: 50,
        },
        (_, status) => {
          if (status === window.google.maps.StreetViewStatus.OK) {
            resolve(true);
          } else {
            resolve(false);
          }
        },
      );
    });
  };

  const loadStreetView = async (): Promise<void> => {
    try {
      await googleIsLoaded().then(async () => {
        const available = await checkStreetViewAvailability();
        setIsStreetViewAvailable(available);
        if (!available) dispatch(addToastNotification(StreetViewUnavailableToast));
      });
    } catch (error) {
      setIsStreetViewAvailable(false);
      dispatch(addToastNotification(StreetViewUnavailableToast));
    }
  };

  useEffect(() => {
    loadStreetView();
  }, [lat, lng]);

  if (!isStreetViewAvailable)
    return (
      <div data-test-id="street-view-container-hidden" className="absolute h-0 w-0 overflow-hidden">
        <GoogleMapsStreetView
          center={{ lat, lng }}
          markerData={markers?.map((marker, index) => ({ id: index, ...marker }))}
        />
      </div>
    );

  return (
    <div
      data-test-id="street-view-container"
      className={cx('absolute w-full z-20', {
        'h-full inset-0': isFullscreen,
        'h-1/2 bottom-0': !isFullscreen,
      })}
    >
      <div className="absolute flex flex-col items-center gap-3 top-[22px] right-4 z-20">
        <IconButton
          icon={closeSvg}
          name="Close"
          handleOnClick={close}
          theme={Theme.PRIMARY}
          buttonSize={ButtonSize.LARGE}
        />
        <IconButton
          icon={fullscreenSvg}
          name="Fullscreen"
          handleOnClick={() => setIsFullscreen(!isFullscreen)}
          theme={Theme.PRIMARY}
          buttonSize={ButtonSize.LARGE}
        />
      </div>

      <GoogleMapsStreetView
        center={{ lat, lng }}
        markerData={markers?.map((marker, index) => ({ id: index, ...marker }))}
        onPositionChange={onPositionChange}
      />
    </div>
  );
};
